/**
 * Recharts Pie/Donut implementation. Hover-highlight via activeShape; label
 * threshold via createPieLabel(minLabelPercent). Donut variant sets an
 * innerRadius.
 */

import { useState } from 'react';
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { PieChartProps, CommonChartProps } from '../types';
import { chartTheme } from '../theme';
import { createPieLabel, renderActiveShape } from '@/utils/calculations';
import { DrillAwareTooltip } from '@/components/analytics/DrillAwareTooltip';

type Props = PieChartProps & CommonChartProps;

export function RechartsPie({ type, data, minLabelPercent = 0.08, enableHover = true }: Props): JSX.Element {
  const theme = chartTheme('recharts');
  const palette = theme.theme.categoricalPalette;
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);
  const labelRenderer = createPieLabel(minLabelPercent);

  const isDonut = type === 'donut';

  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsPieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="label"
          cx="50%"
          cy="50%"
          outerRadius="80%"
          innerRadius={isDonut ? '50%' : 0}
          label={labelRenderer}
          labelLine={false}
          activeIndex={enableHover ? activeIndex : undefined}
          activeShape={enableHover ? renderActiveShape : undefined}
          onMouseEnter={enableHover ? (_, idx) => setActiveIndex(idx) : undefined}
          onMouseLeave={enableHover ? () => setActiveIndex(undefined) : undefined}
        >
          {data.map((d, i) => (
            <Cell key={`${d.label}-${i}`} fill={(d.color as string) ?? palette[i % palette.length]} />
          ))}
        </Pie>
        <Tooltip
          content={({ active, payload }) =>
            active && payload && payload.length > 0 ? (
              <DrillAwareTooltip
                rows={[
                  {
                    color: (payload[0].payload.color as string) ?? palette[0],
                    label: String(payload[0].name ?? ''),
                    value: payload[0].value as number,
                  },
                ]}
              />
            ) : null
          }
        />
      </RechartsPieChart>
    </ResponsiveContainer>
  );
}
